import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { assignmentsApi, coursesApi, orgApi, resultsApi } from '../../api/client';

type RankingMetric = 'courses_completed' | 'average_score' | 'fastest_completion' | 'training_hours';
type Props = { storeId?: string | null; onBack?: () => void };

const SETTINGS_KEY = 'leaderboard_settings';
const METRIC_LABELS: Record<RankingMetric, string> = {
  courses_completed: 'Courses Completed',
  average_score: 'Average Quiz Score',
  training_hours: 'Training Hours',
  fastest_completion: 'Fastest Completion',
};
const BADGE_COLORS: Record<string, any> = {
  first_to_complete: 'primary',
  perfect_score: 'warning',
  speed_learner: 'info',
  training_champion: 'success',
};
const BADGE_FILL: Record<string, string> = {
  first_to_complete: '#1976d2',
  perfect_score: '#ed6c02',
  speed_learner: '#0288d1',
  training_champion: '#2e7d32',
};

function hoursFromDuration(duration?: string | null) {
  if (!duration) return 0;
  const parts = duration.toLowerCase().match(/(\d+(?:\.\d+)?)\s*(hour|hr|h|minute|min|m)/g) || [];
  let minutes = 0;
  parts.forEach((part) => {
    const amount = parseFloat(part);
    if (/(hour|hr|h)/.test(part)) minutes += amount * 60;
    else minutes += amount;
  });
  return minutes / 60;
}

function isDone(assignment: any) {
  return !!(assignment?.isTrainingComplete || assignment?.status === 'completed');
}

function badgeIcon(type: string) {
  if (type === 'first_to_complete') return <EmojiEventsIcon fontSize="small" />;
  if (type === 'perfect_score') return <StarIcon fontSize="small" />;
  if (type === 'speed_learner') return <SpeedIcon fontSize="small" />;
  if (type === 'training_champion') return <MilitaryTechIcon fontSize="small" />;
  return <SchoolIcon fontSize="small" />;
}

function MetricTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #ccc', borderRadius: 1, p: 1.25, boxShadow: 1 }}>
      <Typography variant="body2" fontWeight={700}>{row.email || row.name}</Typography>
      {payload.map((entry: any) => (
        <Typography key={entry.name} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </Typography>
      ))}
    </Box>
  );
}

const TrainingLeaderboardPage: React.FC<Props> = ({ storeId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    visibleToEmployees: true,
    metrics: ['courses_completed', 'average_score', 'training_hours'] as RankingMetric[],
  });
  const [rankingMetric, setRankingMetric] = useState<RankingMetric>('courses_completed');
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'all'>('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [anonymizeNames, setAnonymizeNames] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [employeeRows, assignmentRows, resultRows, courseRows] = await Promise.all([
        orgApi.listEmployees(storeId ? { storeId } : undefined),
        assignmentsApi.list(),
        resultsApi.list(),
        coursesApi.list(),
      ]);
      const employeeIds = new Set((employeeRows as any[]).map((employee) => employee.id));
      const scopedAssignments = storeId
        ? (assignmentRows as any[]).filter((item) => employeeIds.has(item.employeeId))
        : (assignmentRows as any[]);
      const assignmentIds = new Set(scopedAssignments.map((item) => item.id));
      setEmployees(employeeRows as any[]);
      setAssignments(scopedAssignments);
      setResults((resultRows as any[]).filter((item) => assignmentIds.has(item.assignmentId)));
      setCourses((courseRows as any[]).map((course) => ({ id: course.id, title: course.title, duration: course.duration })));
      setUpdatedAt(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) setSettings(JSON.parse(saved));
    } catch {
      /* keep defaults */
    }
  }, [load]);

  const saveSettings = (next: typeof settings) => {
    setSettings(next);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  };

  const badges = useMemo(() => {
    const map = new Map<string, any[]>();
    const add = (employeeId: string, badge: any) => {
      const list = map.get(employeeId) || [];
      if (!list.some((item) => item.id === badge.id)) list.push(badge);
      map.set(employeeId, list);
    };
    results.forEach((result) => {
      if (Number(result.score) !== 100) return;
      const assignment = assignments.find((item) => item.id === result.assignmentId);
      if (!assignment?.employeeId) return;
      add(assignment.employeeId, {
        id: `perfect_${result.id}`,
        type: 'perfect_score',
        name: 'Perfect Score',
        description: `Scored 100% on ${assignment.course?.title || 'quiz'}`,
      });
    });
    const firstByCourse = new Map<string, any>();
    assignments
      .filter((item) => isDone(item) && item.trainingCompletedAt)
      .sort((a, b) => new Date(a.trainingCompletedAt).getTime() - new Date(b.trainingCompletedAt).getTime())
      .forEach((item) => {
        if (!firstByCourse.has(item.courseId)) firstByCourse.set(item.courseId, item);
      });
    firstByCourse.forEach((item, courseId) => {
      add(item.employeeId, {
        id: `first_${courseId}`,
        type: 'first_to_complete',
        name: 'First to Complete',
        description: `First employee to complete ${item.course?.title || 'a course'}`,
      });
    });
    assignments.filter((item) => isDone(item) && item.trainingCompletedAt && item.createdAt).forEach((item) => {
      const hours = (new Date(item.trainingCompletedAt).getTime() - new Date(item.createdAt).getTime()) / 3600000;
      if (hours > 0 && hours <= 24) {
        add(item.employeeId, {
          id: `speed_${item.id}`,
          type: 'speed_learner',
          name: 'Speed Learner',
          description: `Completed ${item.course?.title || 'a course'} within 24 hours`,
        });
      }
    });
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    const monthly = new Map<string, number>();
    assignments
      .filter((item) => isDone(item) && item.trainingCompletedAt && new Date(item.trainingCompletedAt) >= monthAgo)
      .forEach((item) => monthly.set(item.employeeId, (monthly.get(item.employeeId) || 0) + 1));
    const championCount = Math.max(0, ...Array.from(monthly.values()));
    if (championCount >= 3) {
      monthly.forEach((count, employeeId) => {
        if (count === championCount) {
          add(employeeId, {
            id: `champion_${employeeId}`,
            type: 'training_champion',
            name: 'Training Champion',
            description: `Completed ${count} courses this month`,
          });
        }
      });
    }
    return map;
  }, [assignments, results]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const start = timePeriod === 'week' ? now - 7 * 86400000 : timePeriod === 'month' ? new Date(new Date().setMonth(new Date().getMonth() - 1)).getTime() : null;
    let scopedAssignments = assignments;
    let scopedResults = results;
    if (start) {
      scopedAssignments = scopedAssignments.filter((item) => item.trainingCompletedAt && new Date(item.trainingCompletedAt).getTime() >= start);
      scopedResults = scopedResults.filter((item) => new Date(item.createdAt).getTime() >= start);
    }
    if (departmentFilter !== 'all') {
      scopedAssignments = scopedAssignments.filter((item) => item.employee?.department === departmentFilter);
    }
    if (courseFilter !== 'all') {
      scopedAssignments = scopedAssignments.filter((item) => item.courseId === courseFilter);
      const ids = new Set(scopedAssignments.map((item) => item.id));
      scopedResults = scopedResults.filter((item) => ids.has(item.assignmentId));
    }
    return { assignments: scopedAssignments, results: scopedResults };
  }, [assignments, results, timePeriod, departmentFilter, courseFilter]);

  const rankings = useMemo(() => {
    const rows = new Map<string, any>();
    employees.forEach((employee) => {
      if (departmentFilter !== 'all' && employee.department !== departmentFilter) return;
      rows.set(employee.id, {
        employee,
        value: 0,
        displayValue: '',
        badges: badges.get(employee.id) || [],
      });
    });
    if (rankingMetric === 'courses_completed') {
      filtered.assignments.filter(isDone).forEach((item) => {
        const row = rows.get(item.employeeId);
        if (row) row.value += 1;
      });
      rows.forEach((row) => { row.displayValue = `${row.value} courses`; });
    } else if (rankingMetric === 'average_score') {
      const scores = new Map<string, number[]>();
      filtered.results.forEach((result) => {
        const assignment = filtered.assignments.find((item) => item.id === result.assignmentId);
        if (!assignment) return;
        const list = scores.get(assignment.employeeId) || [];
        list.push(Number(result.score || 0));
        scores.set(assignment.employeeId, list);
      });
      scores.forEach((list, employeeId) => {
        const row = rows.get(employeeId);
        if (!row || !list.length) return;
        row.value = Math.round(list.reduce((sum, score) => sum + score, 0) / list.length);
        row.displayValue = `${row.value}%`;
      });
    } else if (rankingMetric === 'training_hours') {
      filtered.assignments.filter(isDone).forEach((item) => {
        const row = rows.get(item.employeeId);
        if (row) row.value += hoursFromDuration(item.course?.duration);
      });
      rows.forEach((row) => { row.displayValue = `${row.value.toFixed(1)} hours`; });
    } else {
      const times = new Map<string, number[]>();
      filtered.assignments.filter((item) => isDone(item) && item.trainingCompletedAt).forEach((item) => {
        const hours = (new Date(item.trainingCompletedAt).getTime() - new Date(item.createdAt).getTime()) / 3600000;
        const list = times.get(item.employeeId) || [];
        list.push(Math.max(0, hours));
        times.set(item.employeeId, list);
      });
      times.forEach((list, employeeId) => {
        const row = rows.get(employeeId);
        if (!row || !list.length) return;
        row.value = Math.round((list.reduce((sum, hours) => sum + hours, 0) / list.length) * 10) / 10;
        row.displayValue = `${row.value} hours avg`;
      });
    }
    return Array.from(rows.values())
      .filter((row) => row.value > 0)
      .sort((a, b) => (rankingMetric === 'fastest_completion' ? a.value - b.value : b.value - a.value))
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }, [employees, filtered, rankingMetric, badges, departmentFilter]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((employee) => employee.department).filter(Boolean))).sort(),
    [employees],
  );

  const displayName = (ranking: any) => (anonymizeNames ? `Employee ${ranking.rank}` : ranking.employee.name);

  const topChart = rankings.slice(0, 10).map((ranking) => ({
    name: anonymizeNames ? `Employee ${ranking.rank}` : ranking.employee.name,
    value: ranking.value,
  }));

  const departmentPerformance = useMemo(() => {
    const map = new Map<string, { total: number; ids: Set<string> }>();
    rankings.forEach((ranking) => {
      const department = ranking.employee.department || 'N/A';
      const current = map.get(department) || { total: 0, ids: new Set<string>() };
      current.total += ranking.value;
      current.ids.add(ranking.employee.id);
      map.set(department, current);
    });
    return Array.from(map.entries())
      .map(([department, data]) => ({
        department,
        averageValue: data.ids.size ? Math.round((data.total / data.ids.size) * 10) / 10 : 0,
        employeeCount: data.ids.size,
      }))
      .sort((a, b) => b.averageValue - a.averageValue);
  }, [rankings]);

  const valueDistribution = useMemo(() => {
    const buckets = new Map<string, number>();
    rankings.forEach((ranking) => {
      const size = rankingMetric === 'average_score' ? 10 : 5;
      const start = Math.floor(ranking.value / size) * size;
      const suffix = rankingMetric === 'average_score' ? '%' : rankingMetric === 'courses_completed' ? '' : 'h';
      const key = `${start}-${start + size - 1}${suffix}`;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });
    return Array.from(buckets.entries())
      .map(([range, count]) => ({ range, count }))
      .sort((a, b) => parseInt(a.range, 10) - parseInt(b.range, 10));
  }, [rankings, rankingMetric]);

  const badgeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    badges.forEach((list) => list.forEach((badge) => { counts[badge.type] = (counts[badge.type] || 0) + 1; }));
    const names: Record<string, string> = {
      first_to_complete: 'First to Complete',
      perfect_score: 'Perfect Score',
      speed_learner: 'Speed Learner',
      training_champion: 'Training Champion',
    };
    return Object.entries(counts).map(([type, value]) => ({ name: names[type] || type, value, color: BADGE_FILL[type] || '#9e9e9e' }));
  }, [badges]);

  const metricComparison = rankings.slice(0, 10).map((ranking) => {
    const scores = results
      .filter((result) => {
        const assignment = assignments.find((item) => item.id === result.assignmentId);
        return assignment?.employeeId === ranking.employee.id;
      })
      .map((result) => Number(result.score || 0));
    const completed = assignments.filter((item) => item.employeeId === ranking.employee.id && isDone(item));
    const hours = completed.reduce((sum, item) => sum + hoursFromDuration(item.course?.duration), 0);
    return {
      name: anonymizeNames ? `Employee ${ranking.rank}` : ranking.employee.name,
      email: anonymizeNames ? `Employee ${ranking.rank}` : ranking.employee.email,
      coursesCompleted: completed.length,
      averageScore: scores.length ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 0,
      trainingHours: Math.round(hours * 10) / 10,
    };
  });

  const chartCard = (title: string, children: React.ReactElement, md: number = 6, height = 300) => (
    <Grid item xs={12} md={md}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>{children}</ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {onBack && <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>Back to Dashboard</Button>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h4" fontWeight={700}>Training Leaderboard</Typography>
        <Box>
          <IconButton color="primary" title="Settings" onClick={() => setSettingsOpen(true)}><SettingsIcon /></IconButton>
          <IconButton color="primary" title="Refresh" onClick={load}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!settings.enabled && <Alert severity="info">Leaderboard is currently disabled. Enable it in settings to view rankings.</Alert>}

      {settings.enabled && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ranking By</InputLabel>
                    <Select value={rankingMetric} label="Ranking By" onChange={(e) => setRankingMetric(e.target.value as RankingMetric)}>
                      {(Object.keys(METRIC_LABELS) as RankingMetric[]).map((metric) => (
                        <MenuItem key={metric} value={metric}>{METRIC_LABELS[metric]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time Period</InputLabel>
                    <Select value={timePeriod} label="Time Period" onChange={(e) => setTimePeriod(e.target.value as any)}>
                      <MenuItem value="week">This Week</MenuItem>
                      <MenuItem value="month">This Month</MenuItem>
                      <MenuItem value="all">All Time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Department</InputLabel>
                    <Select value={departmentFilter} label="Department" onChange={(e) => setDepartmentFilter(e.target.value)}>
                      <MenuItem value="all">All Departments</MenuItem>
                      {departments.map((department) => <MenuItem key={department} value={department}>{department}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Course</InputLabel>
                    <Select value={courseFilter} label="Course" onChange={(e) => setCourseFilter(e.target.value)}>
                      <MenuItem value="all">All Courses</MenuItem>
                      {courses.map((course) => <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={anonymizeNames} onChange={(e) => setAnonymizeNames(e.target.checked)} />} label="Anonymize Employee Names" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {chartCard('Top 10 Performers', (
              <BarChart data={topChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="value" fill="#1976d2" name={METRIC_LABELS[rankingMetric]} />
              </BarChart>
            ), 8)}
            {chartCard('Badge Distribution', (
              <PieChart>
                <Pie data={badgeDistribution} dataKey="value" nameKey="name" outerRadius={90} label={(entry: any) => `${entry.name}: ${entry.value}`}>
                  {badgeDistribution.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <ChartTooltip />
                <Legend />
              </PieChart>
            ), 4)}
            {chartCard('Department Performance', (
              <BarChart data={departmentPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" angle={-35} textAnchor="end" height={70} />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="averageValue" fill="#1976d2" name="Average Value" />
                <Bar dataKey="employeeCount" fill="#e91e63" name="Employee Count" />
              </BarChart>
            ))}
            {chartCard('Value Distribution', (
              <BarChart data={valueDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="count" fill="#1976d2" name="Number of Employees" />
              </BarChart>
            ))}
            {chartCard('Top Performers - Multi-Metric Comparison', (
              <BarChart data={metricComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} />
                <YAxis />
                <ChartTooltip content={<MetricTooltip />} />
                <Legend />
                <Bar dataKey="averageScore" fill="#2e7d32" name="Average Score (%)" />
                <Bar dataKey="coursesCompleted" fill="#1976d2" name="Courses Completed" />
                <Bar dataKey="trainingHours" fill="#ed6c02" name="Training Hours" />
              </BarChart>
            ), 12)}
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                <Typography variant="h6">Top Performers - {METRIC_LABELS[rankingMetric]}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {updatedAt.toLocaleString('en-GB')}
                </Typography>
              </Box>
              {rankings.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No rankings available for the selected filters</Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell>Badges</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankings.map((ranking) => (
                        <TableRow
                          key={ranking.employee.id}
                          sx={{
                            bgcolor: ranking.rank === 1 ? 'rgba(255, 215, 0, 0.18)' : ranking.rank === 2 ? 'rgba(192, 192, 192, 0.25)' : ranking.rank === 3 ? 'rgba(205, 127, 50, 0.16)' : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight={700}>{ranking.rank <= 3 ? ['🥇', '🥈', '🥉'][ranking.rank - 1] : `#${ranking.rank}`}</Typography>
                              {ranking.rank <= 3 && <EmojiEventsIcon color="primary" fontSize="small" />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ bgcolor: 'grey.400', width: 36, height: 36 }}><PersonIcon /></Avatar>
                              <Box>
                                <Typography fontWeight={600}>{displayName(ranking)}</Typography>
                                {!anonymizeNames && <Typography variant="caption" color="text.secondary">{ranking.employee.email}</Typography>}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{ranking.employee.department || 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Typography color="primary" fontWeight={700}>{ranking.displayValue}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {ranking.badges.slice(0, 3).map((badge: any) => (
                                <Tooltip key={badge.id} title={badge.description}>
                                  <Chip icon={badgeIcon(badge.type)} label={badge.name} color={BADGE_COLORS[badge.type]} size="small" />
                                </Tooltip>
                              ))}
                              {ranking.badges.length > 3 && <Chip label={`+${ranking.badges.length - 3}`} size="small" variant="outlined" />}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {chartCard('Performance Distribution', (
              <BarChart data={rankings.slice(0, 20).map((ranking) => ({ rank: ranking.rank, value: ranking.value }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rank" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="value" fill="#e91e63" name={METRIC_LABELS[rankingMetric]} />
              </BarChart>
            ))}
            {chartCard('Top 3 Performers Comparison', (
              <BarChart data={rankings.slice(0, 3).map((ranking) => ({ name: displayName(ranking), value: ranking.value }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="value" fill="#2e7d32" name={METRIC_LABELS[rankingMetric]} />
              </BarChart>
            ))}
            {chartCard('Department Leaderboard Comparison', (
              <BarChart data={departmentPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" angle={-35} textAnchor="end" height={70} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <ChartTooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="averageValue" fill="#1976d2" name="Average Value" />
                <Bar yAxisId="right" dataKey="employeeCount" fill="#0288d1" name="Participating Employees" />
              </BarChart>
            ), 12, 350)}
            {chartCard('Comprehensive Performance Metrics (Top 10)', (
              <BarChart data={metricComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <ChartTooltip content={<MetricTooltip />} />
                <Legend />
                <Bar yAxisId="left" dataKey="averageScore" fill="#2e7d32" name="Average Score (%)" />
                <Bar yAxisId="left" dataKey="coursesCompleted" fill="#1976d2" name="Courses Completed" />
                <Bar yAxisId="right" dataKey="trainingHours" fill="#ed6c02" name="Training Hours" />
              </BarChart>
            ), 12, 350)}
            {chartCard('Value Range Distribution', (
              <BarChart data={valueDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="count" fill="#1976d2" name="Number of Employees" />
              </BarChart>
            ))}
            {chartCard('Top Performer by Department', (
              <BarChart data={departmentPerformance.map((department) => ({
                department: department.department,
                averageValue: department.averageValue,
                topValue: rankings.find((ranking) => (ranking.employee.department || 'N/A') === department.department)?.value || 0,
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" angle={-35} textAnchor="end" height={70} />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="averageValue" fill="#1976d2" name="Department Average" />
                <Bar dataKey="topValue" fill="#2e7d32" name="Top Performer Value" />
              </BarChart>
            ))}
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Badge Achievements</Typography>
              {badges.size === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No badges have been earned yet. Badges are awarded for perfect scores, first completions, and training milestones.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {Array.from(badges.entries()).map(([employeeId, list]) => {
                    const employee = employees.find((item) => item.id === employeeId);
                    if (!employee || list.length === 0) return null;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={employeeId}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Avatar sx={{ bgcolor: '#1976d2' }}><PersonIcon /></Avatar>
                              <Typography fontWeight={600}>{anonymizeNames ? 'Employee' : employee.name}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {list.map((badge) => (
                                <Tooltip key={badge.id} title={badge.description}>
                                  <Chip icon={badgeIcon(badge.type)} label={badge.name} color={BADGE_COLORS[badge.type]} size="small" />
                                </Tooltip>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Leaderboard Settings</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={<Switch checked={settings.enabled} onChange={(e) => saveSettings({ ...settings, enabled: e.target.checked })} />}
            label="Enable Leaderboard"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the leaderboard will be visible and rankings will be calculated.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Switch checked={settings.visibleToEmployees} disabled={!settings.enabled} onChange={(e) => saveSettings({ ...settings, visibleToEmployees: e.target.checked })} />}
            label="Visible to Employees"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, employees can see the leaderboard. When disabled, only managers can view it.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Displayed Metrics</Typography>
          {(Object.keys(METRIC_LABELS) as RankingMetric[]).map((metric) => (
            <FormControlLabel
              key={metric}
              control={(
                <Switch
                  checked={settings.metrics.includes(metric)}
                  disabled={!settings.enabled}
                  onChange={(e) => {
                    const metrics = e.target.checked
                      ? [...settings.metrics, metric]
                      : settings.metrics.filter((item) => item !== metric);
                    saveSettings({ ...settings, metrics });
                  }}
                />
              )}
              label={METRIC_LABELS[metric]}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingLeaderboardPage;
